<template>
    <div v-if="loaded">
        <div v-if="shouldDisplayLicenseRecapContainer" class="license-recap__container">
            <Card
                background="lightgrey"
                borderColor="gray-40"
                :padding="4"
                class="subscription-overview__card"
            >
                <CompassAlert
                    v-if="shouldDisplayAlertSubscription"
                    type="warning"
                    :description="descriptionAlertSubscription"
                    :showClose="false"
                    :action="actionAlertSubscription"
                />
                <div class="subscription-overview__card-information">
                    <div class="subscription-overview__summary">
                        <div class="subscription-overview__summary-plan">
                            <div class="typographies-paragraph-medium-bold">
                                {{
                                    $t.__(
                                        'sellsyaccountmanager.SubscriptionManager.Overview.label_plan_subscription',
                                    )
                                }}
                            </div>
                            <div
                                class="subscription-overview__summary-plan-tag typographies-paragraph-caps-medium-semibold"
                            >
                                {{ plan }}
                            </div>
                        </div>

                        <div class="subscription-overview__summary-date">
                            <div class="typographies-paragraph-medium-bold">
                                {{
                                    $t.__(
                                        'sellsyaccountmanager.SubscriptionManager.Overview.label_date_subscription',
                                    )
                                }}
                            </div>
                            <div class="typographies-paragraph-medium-regular">
                                {{ period }}
                            </div>
                            <SubscriptionTag
                                v-if="canCancelSubscription"
                                :label="
                                    $t.__(
                                        'sellsyaccountmanager.SubscriptionManager.Overview.yearly_contract_term',
                                    )
                                "
                                variant="renew"
                            />
                        </div>

                        <div class="subscription-overview__summary-recurring">
                            <div class="typographies-paragraph-medium-bold">
                                {{
                                    $t.__(
                                        'sellsyaccountmanager.SubscriptionManager.Overview.label_recurring_subscription',
                                    )
                                }}
                            </div>
                            <div class="typographies-paragraph-medium-regular">
                                {{ type }}
                            </div>
                        </div>
                    </div>
                    <div class="aright">
                        <template v-if="!isChargebeeProvider">
                            <div class="flex-row flex-right">
                                <div class="fs-10 bold">
                                    {{
                                        amount.total
                                            | floatNumber
                                            | numberformatWithOptions(numformat)
                                    }}
                                </div>
                                <div class="flex-column ov-hidden mt-1">
                                    <div class="bold">
                                        <span class="fs-4">
                                            {{ numformat.currencysymbol }}
                                        </span>
                                        <span class="fs-3">
                                            {{ $l10n.__('HT') }}
                                        </span>
                                    </div>
                                    <div class="grey fs-3">/{{ intervalName }}</div>
                                </div>
                            </div>
                            <div class="fs-2">
                                {{ discount }}
                            </div>
                        </template>

                        <div class="subscription-overview__actions-subscription">
                            <CompassTooltip
                                v-if="shouldDisplayEditSubscriptionBtn"
                                :disabled="!disableChargebeeUpdateSubscription"
                                :content="
                                    $t.__(
                                        'sellsyaccountmanager.SubscriptionManager.Overview.self_service_inaccessible_by_transfer',
                                    )
                                "
                            >
                                <CompassButton
                                    class="license-overview__btn-edit-subscription"
                                    :label="$t.__('common.actions.edit_subscription')"
                                    :disabled="
                                        hasRenewCart ||
                                        disableChargebeeUpdateSubscription ||
                                        renewScheduled.hasRenewScheduled
                                    "
                                    @click="upsellCart"
                                />
                            </CompassTooltip>
                            <CompassButton
                                v-if="canCancelSubscription"
                                :label="$t.__('common.actions.cancel_subscription')"
                                type="tertiary"
                                @click="handleCancelSubscription"
                            />
                        </div>
                    </div>
                </div>
            </Card>
            <ManageChargebeeAccount v-if="isChargebeeProvider" />
            <card v-else class="ml-4 wp-4 fs-3 p-4 lh-big" borderColor="gray-40">
                <h5 class="bold m-0 mb-2 dinline">{{ $l10n.__('Information de facturation') }}</h5>
                <div>{{ billingAddress.client_name }}</div>
                <div>{{ billingContact.first_name + ' ' + billingContact.last_name }}</div>
                <div>{{ billingAddress.address_line_1 }}</div>
                <div v-if="billingAddress.address_line_2 != ''">
                    {{ billingAddress.address_line_2 }}
                </div>
                <div>
                    {{ billingAddress.postal_code }} {{ billingAddress.city }}
                    {{ billingAddress.country }}
                </div>

                <div class="grey mt-2">
                    {{ billingContact.email + ' - ' + billingContact.phone_number }}
                </div>
            </card>
        </div>

        <payment-card-manager
            v-if="paymentMethod === 'card' && !isChargebeeProvider"
            class="mt-3"
        />

        <h3 :class="['lh-24', shouldDisplayLicenseRecapContainer ? 'mt-6' : 'mt-0']">
            {{ $l10n.__('Mes licences') }}
        </h3>
        <p class="mt-0 mb-3 lh-20 fs-3">
            {{
                $l10n.__(
                    'Votre offre comporte plusieurs niveaux de licence afin que vos collaborateurs puissent utiliser les différents modules proposés dans Sellsy. Vous pouvez gérer l’attribution des licences à vos collaborateurs dans l’onglet : ',
                )
            }}
            <a class="semibold pointer hover-primary-100" @click="$emit('go-to-staffs')">
                {{ $l10n.__('Gestion des licences.') }}
            </a>
        </p>
        <licences :licenses="licenses" :versionedLicenses="versionedLicenses" />

        <plugins-summary v-if="hasPlugins" :activePlugins="activePlugins" />
        <QuotaSummary
            v-if="loaded"
            :isChargebeeProvider="isChargebeeProvider"
            :chargebeeThirdPartyPayerId="chargebeeThirdPartyPayerId"
            :licenseAssignement="licenseAssignement"
        />
        <CancelSubscriptionCancellationDialog
            v-if="shouldDisplayCancelSubscriptionCancellationDialog"
            :subscriptionId="chargebeeSubscriptionId"
            @onClose="shouldDisplayCancelSubscriptionCancellationDialog = false"
            @onSuccess="fetchChargebeeSubscriptionDatas"
        />
    </div>
</template>

<script>
import Card from 'vcomponents/Card.vue';
import Licences from 'vcomponents/sellsyaccountmanager/Licences.vue';
import { numformatCart } from 'Config/authenticated/dictionary/Pricingv4';
import { createNamespacedHelpers } from 'vuex';
import { goToUrl } from 'js/vue/helpers.js';
import PluginsSummary from 'vcomponents/sellsyaccountmanager/SubscriptionManager/PluginsSummary.vue';
import PaymentCardManager from 'vcomponents/sellsyaccountmanager/SubscriptionManager/PaymentCardManager.vue';
import ManageChargebeeAccount from 'vcomponents/sellsyaccountmanager/SubscriptionManager/ManageChargebeeAccount.vue';
import QuotaSummary from 'vcomponents/sellsyaccountmanager/SubscriptionManager/quota/QuotaSummary.vue';
import SubscriptionTag from 'vcomponents/common/subscription/SubscriptionTag.vue';
import CancelSubscriptionCancellationDialog from 'vcomponents/sellsyaccountmanager/SubscriptionManager/CancelSubscriptionCancellationDialog.vue';
import Feature from 'Services/Feature';
import { fetchSubscription, updateCustomerDomains } from 'api/chargebee/chargebeeService';
const { mapState, mapActions, mapGetters } = createNamespacedHelpers('subscriptionManager');
const { mapState: mapStateUser } = createNamespacedHelpers('user');
const { mapGetters: mapCorpGetters } = createNamespacedHelpers('corp');
export default {
    components: {
        PaymentCardManager,
        Card,
        Licences,
        PluginsSummary,
        ManageChargebeeAccount,
        QuotaSummary,
        SubscriptionTag,
        CancelSubscriptionCancellationDialog,
    },
    props: {
        isChargebeeProvider: {
            type: Boolean,
            required: true,
        },
        chargebeeSubscriptionId: {
            type: String,
            required: false,
            default: '',
        },
        chargebeeThirdPartyPayerId: {
            type: String,
            required: false,
            default: '',
        },
        licenseAssignement: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    events: { 'chargebee:payment:source:add': 'addChargebeePaymentSource' },
    data() {
        return {
            shouldDisplayLicenseRecapContainer: true,
            hasChargebeePaymentMethod: false,
            contractTerm: null,
            chargebeeCustomerId: null,
            shouldDisplayCancelSubscriptionCancellationDialog: false,
            renewScheduled: {
                hasRenewScheduled: false,
                renewScheduledAt: null,
            },
        };
    },
    computed: {
        period: function () {
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            const start = Date.parse(this.serviceStartDate);
            const end = Date.parse(this.serviceEndDate);
            const locale = this.locale.split('.')[0].replace('_', '-');
            if (start === null || end === null) {
                return this.$l10n.__('Dates non disponibles');
            }
            return this.$l10n.__(
                'Du %s au %s',
                start.toLocaleString(locale, options),
                end.toLocaleString(locale, options),
            );
        },
        type: function () {
            return this.interval === 'monthly' ? this.$l10n.__('Mensuel') : this.$l10n.__('Annuel');
        },
        intervalName: function () {
            return this.interval === 'monthly' ? this.$l10n.__('mois') : this.$l10n.__('an');
        },
        discount: function () {
            let amount = this.amount.total_discount;
            if (amount === 0) {
                return '';
            }
            amount = this.$options.filters.floatNumber(amount);
            amount = this.$options.filters.numberformatCurrency(amount);
            return this.$l10n.__('dont %s de remise', amount);
        },
        numformat: function () {
            return numformatCart;
        },
        hasPlugins() {
            return !!this.activePlugins.length;
        },
        shouldDisplayEditSubscriptionBtn() {
            if (!this.getIsSelfService) {
                return false;
            }
            if (this.isChargebeeProvider) {
                return this.chargebeeSubscriptionId;
            }
            return !this.licenseAssignement;
        },
        disableChargebeeUpdateSubscription() {
            return this.isChargebeeProvider && !this.hasChargebeePaymentMethod;
        },
        canCancelSubscription() {
            return this.contractTerm?.status === 'active' && !this.shouldDisplayAlertSubscription;
        },
        shouldDisplayAlertSubscription() {
            return (
                this.contractTerm?.action_at_term_end === 'cancel' ||
                this.renewScheduled?.hasRenewScheduled
            );
        },
        actionAlertSubscription() {
            if (this.renewScheduled?.hasRenewScheduled) {
                return null;
            }
            // If tacit renawal was disabled, it can't be reactivited in the last 30 days of the contract
            if (moment(this.serviceEndDate).diff(moment(), 'days') < 30) {
                return null;
            }

            return {
                label: this.$t.__('common.actions.cancel_subscription_cancellation'),
                callback: this.handleCancelSubscriptionCancellation,
            };
        },
        descriptionAlertSubscription() {
            if (this.renewScheduled?.hasRenewScheduled) {
                if (!this.renewScheduled.renewScheduledAt) {
                    return this.$t.__(
                        'sellsyaccountmanager.SubscriptionManager.Overview.label_alert_subscription_renew_scheduled_no_date',
                    );
                }
                const dateFormatted = this.$options.filters.saasDateFormatted(
                    moment.unix(this.renewScheduled.renewScheduledAt),
                );
                return this.$t.__(
                    'sellsyaccountmanager.SubscriptionManager.Overview.label_alert_subscription_renew_scheduled',
                    { date: dateFormatted },
                );
            }
            const format = moment.localeData(this.uiLang).longDateFormat('L');
            const date = moment(this.serviceEndDate).format(format);
            return this.$t.__(
                'sellsyaccountmanager.SubscriptionManager.Overview.label_alert_subscription_canceled',
                { date: date },
            );
        },
        ...mapGetters(['activePlugins', 'hasRenewCart']),
        ...mapState([
            'plan',
            'serviceStartDate',
            'serviceEndDate',
            'interval',
            'amount',
            'billingAddress',
            'billingContact',
            'loaded',
            'plugins',
            'licenses',
            'versionedLicenses',
            'paymentMethod',
        ]),
        ...mapStateUser(['locale', 'uiLang']),
        ...mapCorpGetters(['getIsSelfService']),
    },
    created() {
        this.getSubscription();
        if (this.isChargebeeProvider) {
            this.shouldDisplayLicenseRecapContainer = !this.chargebeeThirdPartyPayerId;
            this.fetchChargebeeSubscriptionDatas();
        }
    },
    mounted() {
        if (localStorage.getItem('chargebee_upsell_update_success')) {
            localStorage.removeItem('chargebee_upsell_update_success');
            this.$pushToast({
                type: 'success',
                icon: true,
                text: this.$l10n.__(
                    'Paiement effectué, vos modifications d’abonnement seront actives d’ici quelques minutes',
                ),
                close: true,
            });
        }
        const domains = JSON.parse(localStorage.getItem('chargebee_upsell_domains'));
        if (domains) {
            localStorage.removeItem('chargebee_upsell_domains');
            updateCustomerDomains(domains);
        }
    },
    beforeDestroy() {
        window.ChargebeeGlobal.clearChargebeeScript();
    },
    methods: {
        ...mapActions(['getSubscription']),
        async upsellCart() {
            if (this.isChargebeeProvider) {
                //Redirection to selfservice upsell cart
                if (Feature.isEnabled('chargebee-upsell-selfservice')) {
                    goToUrl(
                        this.$router.resolve({
                            name: 'chargebee.upsell.cart',
                        }).href,
                    );
                } else {
                    const params = {
                        subscriptionId: this.chargebeeSubscriptionId,
                    };
                    await window.ChargebeeGlobal.openCbPortalSection(
                        'SUBSCRIPTION_DETAILS',
                        params,
                    );
                }
            } else {
                goToUrl(
                    this.$router.resolve({
                        name: 'sam.v4.client.upsell.form.plan',
                        params: { upsellCartId: 'new' },
                    }).href,
                );
            }
        },
        /**
         * If cart was paid with payment method as 'banktransfert' and no credit card or SEPA data is stored
         * then 'subscription.data.customer.payment_method' does not exist.
         */
        fetchChargebeeSubscriptionDatas() {
            fetchSubscription().then((subscription) => {
                this.hasChargebeePaymentMethod = !!subscription.data.customer.payment_method;
                this.contractTerm = subscription.data.subscription?.contract_term;
                this.chargebeeCustomerId = subscription.data.customer?.id;
                this.renewScheduled = {
                    hasRenewScheduled: subscription.data.subscription?.has_scheduled_changes,
                    renewScheduledAt: subscription.data.subscription?.changes_scheduled_at,
                };
            });
        },
        // eslint-disable-next-line vue/no-unused-properties
        addChargebeePaymentSource() {
            this.hasChargebeePaymentMethod = true;
        },
        async handleCancelSubscription() {
            try {
                await window.ChargebeeGlobal.initChargebeeScript();
                const cbPortal = await window.ChargebeeGlobal.initCbPortal(
                    this.chargebeeCustomerId,
                );
                cbPortal.open(
                    {
                        close: () => {
                            this.fetchChargebeeSubscriptionDatas();
                        },
                    },
                    {
                        sectionType: 'sub_cancel',
                        params: {
                            subscriptionId: this.chargebeeSubscriptionId,
                        },
                    },
                );
            } catch (e) {
                console.error(e);
            }
        },
        handleCancelSubscriptionCancellation() {
            this.shouldDisplayCancelSubscriptionCancellationDialog = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.licence-item {
    height: 180px;
}

.fs-10 {
    font-size: 40px;
    line-height: 48px;
}

.lh-20 {
    line-height: 20px;
}

.leading-5 {
    line-height: 16px;
}

.license-recap__container {
    display: flex;
    gap: var(--spacing-3);
    justify-content: space-between;

    .license-overview__btn-edit-subscription {
        margin-top: var(--spacing-3);
    }
}

.subscription-overview {
    &__card {
        flex-basis: 60%;
        height: fit-content;
        padding: var(--spacing-4);

        &-information {
            display: flex;
            justify-content: space-between;
        }
    }

    &__summary {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);

        &-plan-tag {
            background-color: var(--colors-sellsy-brand-secondary-green);
            border-radius: var(--spacing-0);
            color: var(--colors-sellsy-brand-secondary-blue);
            padding: var(--spacing-0) var(--spacing-1);
            width: fit-content;
        }
    }

    &__actions-subscription {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
    }
}
</style>
<style lang="scss">
#cb-container {
    background: #00051999 !important;
}
</style>
